import { toAddressPayload } from '@/utils/address';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/useAuthStore.ts';
import { useEmployeeStore } from '../../store/useEmployeeStore.ts';
import { useToastStore } from '../../store/useToastStore.ts';
import { Card } from '../../components/Shared/Card.tsx';
import { Loading } from '../../components/Shared/Loading.tsx';
import { Button } from '../../components/Shared/Button.tsx';
import { Input } from '../../components/Shared/Input.tsx';
import { FormField } from '../../components/Shared/FormField.tsx';
import { PasswordCriteria } from '../../components/Shared/PasswordCriteria.tsx';
import { Badge } from '../../components/Shared/Badge.tsx';
import { isPasswordValid } from '../../utils/password.ts';
import { User, Lock, Save } from 'lucide-react';

export function Profile() {
  const { t } = useTranslation('profile');
  const { employee } = useAuthStore();
  const { currentEmployee, loading, fetchEmployeeById, updateOwnProfile, changeOwnPassword } = useEmployeeStore();
  const { addToast } = useToastStore();

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    birthDate: '',
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    if (employee?.id) {
      fetchEmployeeById(employee.id);
    }
  }, [employee, fetchEmployeeById]);

  useEffect(() => {
    if (currentEmployee) {
      setProfileData({
        firstName: currentEmployee.firstName || '',
        lastName: currentEmployee.lastName || '',
        email: currentEmployee.email || '',
        phone: currentEmployee.phone || '',
        birthDate: currentEmployee.birthDate 
          ? new Date(currentEmployee.birthDate).toISOString().split('T')[0] 
          : '',
      });
    }
  }, [currentEmployee]);

  const validateProfileForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!profileData.firstName.trim()) {
      errors.firstName = t('validation.firstNameRequired');
    }
    if (!profileData.lastName.trim()) {
      errors.lastName = t('validation.lastNameRequired');
    }
    if (!profileData.email.trim()) {
      errors.email = t('validation.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = t('validation.emailInvalid');
    }

    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validatePasswordForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!passwordData.oldPassword) {
      errors.oldPassword = t('validation.currentPasswordRequired');
    }
    if (!passwordData.newPassword) {
      errors.newPassword = t('validation.newPasswordRequired');
    } else if (!isPasswordValid(passwordData.newPassword)) {
      errors.newPassword = t('validation:password.invalid');
    }
    if (!passwordData.confirmPassword) {
      errors.confirmPassword = t('validation.confirmPasswordRequired');
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = t('validation.passwordsNotMatch');
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateProfileForm()) {
      return;
    }

    setIsSavingProfile(true);
    try {
      await updateOwnProfile({
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        email: profileData.email,
        phone: profileData.phone,
        birthDate: profileData.birthDate || undefined,
        // why: this form does not edit the address — echo the stored one back so saving the
        // profile leaves it untouched (omitted when the employee has none).
        address: toAddressPayload(currentEmployee?.address),
      });
      addToast(t('messages.profileUpdated'), 'success');
      if (employee?.id) {
        await fetchEmployeeById(employee.id);
      }
    } catch {
      addToast(t('messages.profileUpdateError'), 'error');
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validatePasswordForm()) {
      return;
    }

    setIsSavingPassword(true);
    try {
      await changeOwnPassword({
        oldPassword: passwordData.oldPassword,
        newPassword: passwordData.newPassword,
      });
      addToast(t('messages.passwordChanged'), 'success');
      setPasswordData({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setPasswordErrors({});
    } catch {
      addToast(t('messages.passwordChangeError'), 'error');
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleProfileChange = (field: string, value: string) => {
    setProfileData((prev) => ({ ...prev, [field]: value }));
    if (profileErrors[field]) {
      setProfileErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
    if (passwordErrors[field]) {
      setPasswordErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const getRoleBadge = (role: string | null | undefined) => {
    return role === 'Verwaltung' 
      ? <Badge variant="info">{t('roles.verwaltung')}</Badge>
      : <Badge>{t('roles.mitarbeiter')}</Badge>;
  };

  const getStatusBadge = (status: string | null | undefined) => {
    switch (status) {
      case 'Active':
        return <Badge variant="success">{t('status.active')}</Badge>;
      case 'Inactive':
        return <Badge variant="warning">{t('status.inactive')}</Badge>;
      case 'Terminated':
        return <Badge variant="danger">{t('status.terminated')}</Badge>;
      default:
        return <Badge>{t('status.unknown')}</Badge>;
    }
  };

  if (loading && !currentEmployee) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {t('subtitle')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Profile & Password */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Information */}
          <Card>
            <div className="flex items-center gap-3 mb-6">
              <User className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('personalInfo')}
              </h2>
            </div>

            <form onSubmit={handleProfileSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label={t('fields.firstName')} required error={profileErrors.firstName}>
                  <Input
                    value={profileData.firstName}
                    onChange={(e) => handleProfileChange('firstName', e.target.value)}
                    disabled={isSavingProfile}
                  />
                </FormField>

                <FormField label={t('fields.lastName')} required error={profileErrors.lastName}>
                  <Input
                    value={profileData.lastName}
                    onChange={(e) => handleProfileChange('lastName', e.target.value)}
                    disabled={isSavingProfile}
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label={t('fields.email')} required error={profileErrors.email}>
                  <Input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => handleProfileChange('email', e.target.value)}
                    disabled={isSavingProfile}
                  />
                </FormField>

                <FormField label={t('fields.phone')} error={profileErrors.phone}>
                  <Input
                    type="tel"
                    value={profileData.phone}
                    onChange={(e) => handleProfileChange('phone', e.target.value)}
                    disabled={isSavingProfile}
                  />
                </FormField>
              </div>

              <FormField label={t('fields.birthDate')} error={profileErrors.birthDate}>
                <Input
                  type="date"
                  value={profileData.birthDate}
                  onChange={(e) => handleProfileChange('birthDate', e.target.value)}
                  disabled={isSavingProfile}
                />
              </FormField>

              <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button type="submit" disabled={isSavingProfile}>
                  <Save className="w-4 h-4" />
                  {isSavingProfile ? t('actions.saving') : t('actions.saveChanges')}
                </Button>
              </div>
            </form>
          </Card>

          {/* Change Password */}
          <Card>
            <div className="flex items-center gap-3 mb-6">
              <Lock className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {t('changePassword')}
              </h2>
            </div>

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <FormField label={t('fields.currentPassword')} required error={passwordErrors.oldPassword}>
                <Input
                  type="password"
                  value={passwordData.oldPassword}
                  onChange={(e) => handlePasswordChange('oldPassword', e.target.value)}
                  disabled={isSavingPassword}
                  placeholder={t('placeholders.currentPassword')}
                />
              </FormField>

              <FormField label={t('fields.newPassword')} required error={passwordErrors.newPassword}>
                <Input
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                  disabled={isSavingPassword}
                  placeholder={t('placeholders.newPassword')}
                />
                <PasswordCriteria password={passwordData.newPassword} />
              </FormField>

              <FormField label={t('fields.confirmPassword')} required error={passwordErrors.confirmPassword}>
                <Input
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                  disabled={isSavingPassword}
                  placeholder={t('placeholders.confirmPassword')}
                />
              </FormField>

              <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button type="submit" disabled={isSavingPassword}>
                  <Lock className="w-4 h-4" />
                  {isSavingPassword ? t('actions.changing') : t('actions.changePassword')}
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {/* Sidebar - Account Info */}
        <div>
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('accountInfo')}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('fields.personalNumber')}
                </label>
                <p className="text-gray-900 dark:text-white font-mono text-sm mt-1">
                  {currentEmployee?.personalNumber || '—'}
                </p>
              </div>

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('fields.name')}
                </label>
                <p className="text-gray-900 dark:text-white font-medium mt-1">
                  {currentEmployee?.fullName || '—'}
                </p>
              </div>

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('fields.role')}
                </label>
                <div className="mt-1">
                  {getRoleBadge(currentEmployee?.role)}
                </div>
              </div>

              <div>
                <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {t('fields.status')}
                </label>
                <div className="mt-1">
                  {getStatusBadge(currentEmployee?.status)}
                </div>
              </div>

              {currentEmployee?.hireDate && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('fields.hireDate')}
                  </label>
                  <p className="text-gray-900 dark:text-white mt-1">
                    {new Date(currentEmployee.hireDate).toLocaleDateString('de-DE', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              )}

              {currentEmployee?.lastLoginAt && (
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {t('fields.lastLogin')}
                  </label>
                  <p className="text-gray-900 dark:text-white mt-1">
                    {new Date(currentEmployee.lastLoginAt).toLocaleString('de-DE')}
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
